"use client";
import { useCallback, useEffect, useState } from "react";

/** False during SSR and on the first client render, so hydration matches. */
export function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReduced(query.matches);

    const onChange = (event: MediaQueryListEvent) => setPrefersReduced(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return prefersReduced;
}

export interface SlideshowOptions {
  /** Number of slides. 0 or 1 means the slideshow is inert. */
  count: number;
  intervalMs: number;
  /** Caller-specific pause conditions (hover, an expanded card, wrong layout). */
  paused?: boolean;
}

export interface Slideshow {
  index: number;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  prefersReducedMotion: boolean;
  /** Whether auto-advance is actually running right now. */
  playing: boolean;
  /** Explicit play/pause. Resumes even after manual navigation stopped it. */
  setPlaying: (next: boolean) => void;
}

/**
 * The rotation state machine every auto-advancing slideshow shares: interval
 * timer, wrap-around index, manual navigation, and reduced-motion handling.
 * Rendering stays with the caller — a crossfade stack and a sliding track are
 * different presentations of this same state.
 *
 * Reduced motion stops auto-advance; manual navigation still works. Manual
 * navigation also ends auto-advance for good — see `tookControl`.
 */
export function useSlideshow({ count, intervalMs, paused = false }: SlideshowOptions): Slideshow {
  const [index, setIndex] = useState(0);
  // Explicit navigation stops the timer: a visitor who picks a slide is not
  // asking to be moved off it three seconds later. A play control clears it.
  const [autoplayOff, setAutoplayOff] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const rotates = count > 1 && !paused && !autoplayOff && !prefersReducedMotion;

  useEffect(() => {
    if (!rotates) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [rotates, count, intervalMs]);

  // If the slide list shrinks under the current position (e.g. fetched reviews
  // replacing placeholders), snap back to the first slide instead of pointing
  // at a slide that no longer exists.
  useEffect(() => {
    setIndex((current) => (count > 0 && current >= count ? 0 : current));
  }, [count]);

  const goTo = useCallback(
    (target: number) => {
      setAutoplayOff(true);
      setIndex((current) => (count > 0 ? ((target % count) + count) % count : current));
    },
    [count],
  );

  const next = useCallback(() => {
    setAutoplayOff(true);
    setIndex((current) => (count > 0 ? (current + 1) % count : current));
  }, [count]);

  const prev = useCallback(() => {
    setAutoplayOff(true);
    setIndex((current) => (count > 0 ? (current - 1 + count) % count : current));
  }, [count]);

  const setPlaying = useCallback((next: boolean) => {
    setAutoplayOff(!next);
  }, []);

  return { index, next, prev, goTo, prefersReducedMotion, playing: rotates, setPlaying };
}
